import React, { useState, useEffect, lazy, Suspense } from 'react';
import { SecondaryButton } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import Classes from './CarAttributes.module.scss';

import CarAttributesMainGroup from './CarAttributesMainGroup';
import Highlights from './Highlights';
import { TListingCarAttributes, TListingCarDetailGroupedWithIcon, TListingClient } from 'types/TListing';

const CarAttributesTabs = lazy(() => import('./CarAttributesTabs'));
const CarAttributesModal = lazy(() => import('./CarAttributesModal'));

type CarAttributesGroupedWithIconsReduced = {
  mainGroup: TListingCarDetailGroupedWithIcon | null;
  nonMainGroups: TListingCarDetailGroupedWithIcon[];
};

type TCarAttributesProps = {
  carAttributes?: TListingCarAttributes;
  carDetails: TListingClient['carDetails'];
  itemId: TListingClient['itemId'];
  hasDealerHighlights?: boolean;
};

const ONE_COLUMN_ITEMS = 3;
const MAIN_GROUP_KEY = 'Main';
const MIN_WIDTH_FOR_TABS = 768;

const CarAttributes = ({ carAttributes, carDetails, itemId, hasDealerHighlights }: TCarAttributesProps) => {
  const { t } = useI18nContext();

  const [isDesktop, setIsDesktop] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkWindowWidth = () => {
      setIsDesktop(window.innerWidth >= MIN_WIDTH_FOR_TABS);
    };

    // Check initial width
    checkWindowWidth();

    window.addEventListener('resize', checkWindowWidth);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', checkWindowWidth);
  }, []);

  if (!carAttributes) {
    return null;
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const carAttributesGroupedWithIcons = carAttributes.groupedWithIcons || [];
  const { mainGroup, nonMainGroups } = carAttributesGroupedWithIcons.reduce<CarAttributesGroupedWithIconsReduced>(
    (acc, group) => {
      if (group.key === MAIN_GROUP_KEY) {
        acc.mainGroup = group;
        return acc;
      }
      acc.nonMainGroups.push(group);
      return acc;
    },
    { mainGroup: null, nonMainGroups: [] },
  );

  const mainGroupAttributes = mainGroup ? mainGroup.attributes : [];
  const hasMoreAttributes = nonMainGroups.length > 0;
  const listOverviewClassNames = [Classes.list]
    .concat([mainGroupAttributes.length <= ONE_COLUMN_ITEMS ? Classes.oneColumn : ''])
    .join(' ');

  return (
    <section className={Classes.root}>
      <div className={listOverviewClassNames}>
        {mainGroupAttributes.map((item) => (
          <CarAttributesMainGroup item={item} key={item.key} carDetails={carDetails} />
        ))}
      </div>

      {/* Highlights have to be shown in different places based on layout */}
      {isDesktop && hasDealerHighlights && <Highlights itemId={itemId} />}

      {hasMoreAttributes && isDesktop && (
        <Suspense fallback={null}>
          <CarAttributesTabs attributesGroups={nonMainGroups} />
        </Suspense>
      )}

      {hasMoreAttributes && !isDesktop && (
        <SecondaryButton className={Classes.attributesModalTrigger} onClick={handleOpenModal}>
          {t('listing.carDetails.dialog.trigger')}
        </SecondaryButton>
      )}

      {/* Highlights have to be shown in different places based on layout */}
      {!isDesktop && hasDealerHighlights && <Highlights itemId={itemId} />}

      {hasMoreAttributes && isModalOpen && (
        <Suspense fallback={null}>
          <CarAttributesModal handleClose={handleCloseModal} attributesGroups={nonMainGroups} />
        </Suspense>
      )}
    </section>
  );
};

export default CarAttributes;
